<template>
  <header class="bg-black">
    <div class="mx-auto flex h-[89px] max-w-[1280px] items-center justify-between px-6 md:px-10">
      <router-link to="/" :aria-label="t('home')">
        <Logo />
      </router-link>
      <button
        class="rounded-full border border-white/70 px-6 py-1.5 text-lg text-tw-offwhite hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        :lang="isNorwegian ? 'en' : 'no'"
        @click="common.actions.setLanguageCode()"
      >
        {{ t('otherLanguage') }}
      </button>
    </div>
  </header>

  <section class="relative bg-black bg-[url('@/assets/landing/hero-grid.png')] bg-[length:1384px] bg-center text-tw-offwhite" aria-labelledby="hero-heading">
    <div class="absolute inset-0 bg-black/35" aria-hidden="true"></div>
    <div class="relative mx-auto flex max-w-[1280px] flex-col gap-12 px-6 py-16 md:px-[76px] lg:flex-row lg:items-center lg:justify-between lg:py-[84px]">
      <div class="max-w-[531px]">
        <component :is="props.headingTag" id="hero-heading" class="max-w-[419px] text-4xl font-semibold tracking-[-0.02em] md:text-[50px] md:leading-[1.2]">
          {{ t('heading') }}
        </component>
        <p class="mt-6 text-lg leading-8 md:text-[21px] md:leading-9">{{ t('lead') }}</p>
        <div class="mt-9 flex flex-wrap gap-5 md:ml-1.5">
          <template v-if="!user">
            <router-link to="/login" :class="buttonClass" class="bg-tw-orange-cta text-black hover:brightness-110">
              {{ t('login') }}
            </router-link>
            <router-link to="/login?join=true" :class="buttonClass" class="border border-white hover:bg-white/10">
              {{ t('join') }}
            </router-link>
          </template>
          <router-link v-else to="/dashboard" :class="buttonClass" class="bg-tw-orange-cta text-black hover:brightness-110">
            {{ t('dashboard') }}
          </router-link>
        </div>
      </div>
      <div class="w-full max-w-[459px] self-center">
        <HeroAnimation />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  import useCommonStore from '@/composables/common'
  import { useUserStore } from '@/composables/userStore'

  import Logo from '@/components/navigation/LogoView.vue'
  import HeroAnimation from './HeroAnimation.vue'

  // Sub-pages give their own title the h1
  const props = defineProps({
    headingTag: { type: String, default: 'h1' },
  })

  const common = useCommonStore()
  const user = useUserStore().getters.user

  const isNorwegian = computed(() => common.getters.languageCode.value.includes('no'))

  const buttonClass =
    'inline-flex h-12 items-center rounded-[10px] px-6 text-lg font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'

  const { t } = useI18n({
    messages: {
      no: {
        home: 'Startside',
        otherLanguage: 'English',
        heading: 'Få flere engasjert i læringssamtalen',
        lead: 'Samtavla er et gratis, forskningsbasert verktøy for å dele korte innlegg på en felles digital tavle',
        login: 'Logg på',
        join: 'Delta',
        dashboard: 'Gå til mine tavler',
      },
      en: {
        home: 'Home',
        otherLanguage: 'Norsk',
        heading: 'Get more people engaged in the learning conversation',
        lead: 'Talkwall is a free, research-based tool for sharing short posts on a shared digital wall',
        login: 'Log in',
        join: 'Join',
        dashboard: 'Go to my walls',
      },
    },
  })
</script>
